import FormHeader from "@/components/FormHeader";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { getListPeriode } from "@/services/getData";
import { Periode } from "@/types";
import { ChevronRight } from "lucide-react";
import React, { useState } from "react";
import { LuListFilter } from "react-icons/lu";
import { useQuery } from "@tanstack/react-query";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTahun } from "@/hooks/useTahun";

export default function FormFilter({ closeModal }: { closeModal: () => void }) {
  const { selectedTahun } = useTahun();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const idsPeriode = searchParams.get("ids_sys_periode");
  const isPenuh = searchParams.get("is_penuh") || "";
  const tanggalCbt = searchParams.get("tanggal_cbt") || "";
  const jenisRuang = searchParams.get("jenis_ruang") || "";

  const [selectedFilter, setSelectedFilter] = useState("PERIODE");
  const [filter, setFilter] = useState<{
    periode: string[];
    is_penuh: string;
    tanggal_cbt: string;
    jenis_ruang: string;
  }>({
    periode: idsPeriode?.split("-") || [],
    is_penuh: isPenuh,
    tanggal_cbt: tanggalCbt,
    jenis_ruang: jenisRuang,
  });

  const { data: listPeriode, isPending: isPendingPeriode } = useQuery({
    queryKey: ["listPeriode"],
    queryFn: () => getListPeriode(String(selectedTahun?.value)),
  });

  const filteredListPeriode =
    listPeriode && listPeriode?.length > 0
      ? listPeriode?.filter((periode: Periode) => {
          return periode?.jalur_jenis?.is_cbt_internal;
        })
      : listPeriode;

  const handleApplyFilter = (event: React.FormEvent<HTMLFormElement>) => {
    event.preventDefault();

    if (filter.is_penuh.length > 0) {
      searchParams.set("is_penuh", filter.is_penuh);
    } else {
      searchParams.delete("is_penuh");
    }

    if (filter.jenis_ruang.length > 0) {
      searchParams.set("jenis_ruang", filter.jenis_ruang);
    } else {
      searchParams.delete("jenis_ruang");
    }

    if (filter.tanggal_cbt.length > 0) {
      searchParams.set("tanggal_cbt", filter.tanggal_cbt);
    } else {
      searchParams.delete("tanggal_cbt");
    }

    if (filter.periode.length > 0) {
      searchParams.set("ids_sys_periode", filter.periode.join("-"));
    } else {
      searchParams.delete("ids_sys_periode");
    }

    navigate(`/admin/setting/ruang-cbt/detail?${searchParams.toString()}`);
    closeModal();
  };

  return (
    <form
      onSubmit={handleApplyFilter}
      className="pb-20 relative flex flex-col gap-y-3"
    >
      <FormHeader title="Filter Data Rekap" icon={<LuListFilter />} />

      <section className="p-3 relative border rounded-xl flex flex-col gap-y-3">
        <span className="font-bold">Status</span>
        <div className="grid grid-cols-3 gap-3">
          <div className="flex items-center gap-3">
            <Checkbox
              checked={filter.is_penuh == "0"}
              onCheckedChange={(checked) =>
                setFilter((prev) => ({
                  ...prev,
                  is_penuh: checked ? "0" : "",
                }))
              }
            />
            <Label>Kosong</Label>
          </div>
          <div className="flex items-center gap-3">
            <Checkbox
              checked={filter.is_penuh == "2"}
              onCheckedChange={(checked) =>
                setFilter((prev) => ({
                  ...prev,
                  is_penuh: checked ? "2" : "",
                }))
              }
            />
            <Label>Terisi/Belum Penuh</Label>
          </div>
          <div className="flex items-center gap-3">
            <Checkbox
              checked={filter.is_penuh == "1"}
              onCheckedChange={(checked) =>
                setFilter((prev) => ({
                  ...prev,
                  is_penuh: checked ? "1" : "",
                }))
              }
            />
            <Label>Sudah Penuh</Label>
          </div>
          <div className="flex items-center gap-3">
            <Checkbox
              checked={filter.jenis_ruang == "daring"}
              onCheckedChange={(checked) =>
                setFilter((prev) => ({
                  ...prev,
                  jenis_ruang: checked ? "daring" : "",
                }))
              }
            />
            <Label>Daring</Label>
          </div>
          <div className="flex items-center gap-3">
            <Checkbox
              checked={filter.jenis_ruang == "luring"}
              onCheckedChange={(checked) =>
                setFilter((prev) => ({
                  ...prev,
                  jenis_ruang: checked ? "luring" : "",
                }))
              }
            />
            <Label>Luring</Label>
          </div>
          <div className="mt-3 col-span-2">
            <Label>Tanggal CBT</Label>
            <Input
              type="date"
              value={filter.tanggal_cbt}
              onChange={(event) =>
                setFilter((prev) => ({
                  ...prev,
                  tanggal_cbt: event.target.value,
                }))
              }
            />
          </div>
        </div>
      </section>

      <section className="p-3 relative border rounded-xl max-h-[50vh] overflow-auto">
        <div
          className="flex justify-between items-center"
          onClick={() =>
            setSelectedFilter((prev) => (prev === "PERIODE" ? "" : "PERIODE"))
          }
        >
          <span className="font-bold">Periode</span>
          <Button type="button" size={"icon"} variant={"outline"}>
            <ChevronRight
              className={`${
                selectedFilter === "PERIODE" ? "rotate-90" : ""
              } duration-300`}
            />
          </Button>
        </div>
        {selectedFilter === "PERIODE" &&
          (isPendingPeriode ? (
            "Loading..."
          ) : (
            <>
              <div className="mt-3 grid grid-cols-1 gap-5">
                {filteredListPeriode?.map((periode: Periode) => {
                  const isChecked = (filter.periode as string[]).includes(
                    String(periode.id_sys_periode),
                  );

                  return (
                    <div key={periode.id_sys_periode} className="flex gap-3">
                      <Checkbox
                        checked={isChecked}
                        onCheckedChange={(checked) => {
                          setFilter((prev) => {
                            const currentPeriode = [...prev.periode];

                            if (checked) {
                              if (
                                !currentPeriode.includes(
                                  String(periode.id_sys_periode),
                                )
                              ) {
                                currentPeriode.push(
                                  String(periode.id_sys_periode),
                                );
                              }
                            } else {
                              const index = currentPeriode.indexOf(
                                String(periode.id_sys_periode),
                              );
                              if (index > -1) {
                                currentPeriode.splice(index, 1);
                              }
                            }

                            return {
                              ...prev,
                              periode: currentPeriode,
                            };
                          });
                        }}
                      />
                      <Label>
                        {periode.jalur_jenis?.nama_jalur_jenis} Gelombang-
                        {periode.gelombang}
                      </Label>
                    </div>
                  );
                })}
              </div>
            </>
          ))}
      </section>

      <div className="absolute bottom-3 left-3 right-3">
        <div className="flex justify-between">
          <div className="flex gap-3">
            <Button
              onClick={() => closeModal()}
              type="button"
              variant={"outline"}
            >
              Tutup
            </Button>
            <Button>Terapkan Filter</Button>
          </div>
          <Button
            type="button"
            variant={"outline"}
            onClick={() => {
              setFilter((prev) => ({
                ...prev,
                periode: [],
                is_penuh: "",
              }));
              navigate("/admin/setting/ruang-cbt/detail");
            }}
          >
            Reset
          </Button>
        </div>
      </div>
    </form>
  );
}
